// SearchBarWithPersistentResults.tsx
import React, { useMemo, useEffect } from 'react';
import { debounce } from 'lodash';
import parse from 'html-react-parser';
import { TextField, CircularProgress, Typography, Box, Stack, Button, Tabs, Tab } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useSearchContext } from '../context';
import { ChevronRight } from '@mui/icons-material';
import { translate as t } from 'hooks/useTranslations';
import { neutral, primary, warning } from '_lex/_foundations/phxColors';
import { useInView } from 'react-intersection-observer';
import LeftDrawer from '_lex/_layouts/TakeoverLayout/LeftDrawer';
import numeral from 'numeral';
import Loading from 'views/App/Portal/Loading';

const SearchBarWithPersistentResults: React.FC = () => {
  const {
    searchResults,
    getHighlightedText,
    selectedItem,
    setSelectedItem,
    setIsContentLoading,
    searchInputValue,
    setSearchInputValue,
    searchResultsCount,
    searchQuery,
    loading,
    setIsSearchOpen,
    searchType,
    setSearchType,
  } = useSearchContext();
  const navigate = useNavigate();

  const { ref, inView } = useInView();
  const [localInputValue, setLocalInputValue] = React.useState(searchInputValue);

  const debouncedSetSearchInputValue = useMemo(
    () =>
      debounce((value: string) => {
        setSearchInputValue(value);
      }, 500),
    [setSearchInputValue]
  );

  useEffect(() => {
    if (inView && searchQuery.hasNextPage && !searchQuery.isFetchingNextPage) {
      searchQuery.fetchNextPage();
    }
  }, [inView, searchQuery]);

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setLocalInputValue(value);
    debouncedSetSearchInputValue(value);
  };

  // Sync local state with context when it changes externally
  useEffect(() => {
    setLocalInputValue(searchInputValue);
  }, [searchInputValue]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: 'issued' | 'draft') => {
    setSearchType(newValue);
    // Keep search input value but clear selected item and navigate away from current content
    setSelectedItem(null);
    navigate('/admin/search-manuals');
    // Ensure search is triggered for the new search type
    setIsSearchOpen(true);
  };

  const infiniteTrigger = searchQuery.hasNextPage ? (
    <Stack
      ref={ref}
      direction='row'
      spacing={4}
      sx={{
        justifyContent: 'center',
        py: 2,
        visibility: searchQuery.isFetchingNextPage ? 'hidden' : 'visible',
      }}
    >
      <Button
        onClick={() => searchQuery.fetchNextPage()}
        disabled={!searchQuery.hasNextPage || searchQuery.isFetchingNextPage}
        variant='outlined'
      >
        {searchQuery.hasNextPage ? t('common.loadMore') : ''}
      </Button>
    </Stack>
  ) : null;

  const searchHeader = (
    <Stack
      spacing={1}
      sx={{ flexGrow: 1 }}
    >
      <Tabs
        value={searchType}
        onChange={handleTabChange}
        sx={{ mb: 2 }}
      >
        <Tab
          label={t('draftManuals.searchManuals.issuedTab')}
          value='issued'
        />
        <Tab
          label={t('draftManuals.searchManuals.draftTab')}
          value='draft'
        />
      </Tabs>
      <Stack
        spacing={1}
        direction='row'
        sx={{ display: 'grid', gridTemplateColumns: '1fr auto', alignItems: 'center' }}
      >
        <TextField
          fullWidth
          size='small'
          variant='outlined'
          placeholder={t('draftManuals.searchManuals.inputPlaceholder')}
          value={localInputValue}
          onChange={onChange}
          onFocus={() => setIsSearchOpen(true)}
          onBlur={() => setIsSearchOpen(false)}
          sx={{
            '&.MuiFormControl-root': {
              paddingTop: 0,
            },
          }}
        />
        {searchQuery.isLoading && (
          <CircularProgress
            size={20}
            sx={{ mx: 1 }}
          />
        )}
      </Stack>
    </Stack>
  );

  const searchFooter =
    searchInputValue && searchInputValue.length > 2 && searchResultsCount > 0 ? (
      <Typography
        variant='body2'
        color='text.secondary'
      >
        {t('draftManuals.searchManuals.resultsCount', '', { count: numeral(searchResultsCount).format('0,0') })}
      </Typography>
    ) : null;

  const searchContent = (
    <Box
      sx={{
        overflowY: 'auto',
        minHeight: 0, // This is important for Firefox
        height: '100%',
      }}
    >
      {searchQuery.isLoading ? (
        <Loading />
      ) : (
        <>
          {searchResults?.length
            ? searchResults.map((option: any) => {
                const isSelected = selectedItem && selectedItem.contentUri === option.contentUri;

                return (
                  <Box
                    key={option.contentUri}
                    sx={{
                      cursor: 'pointer',
                      backgroundColor: isSelected ? primary[100] : 'transparent',
                      '&:hover': {
                        backgroundColor: neutral[100],
                      },
                    }}
                    onClick={() => {
                      setSelectedItem(option);
                      setIsContentLoading(true);
                      navigate(`/admin/search-manuals?content=${encodeURIComponent(option.contentUri)}`);
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        py: 2,
                        pl: 4,
                        pr: 0,
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        borderBottom: '1px solid lightgrey',
                      }}
                    >
                      <Stack
                        spacing={1}
                        sx={{ minWidth: 0, maxWidth: '100%' }}
                      >
                        <Typography
                          variant='body1'
                          color='primary'
                          sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                        >
                          {`${option.heading ? option.heading : ''} ${option.title}`}
                        </Typography>

                        <Typography
                          variant='caption'
                          color='textSecondary'
                          sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                        >
                          {`(${option.manualName})`}
                        </Typography>
                        <Typography
                          variant='caption'
                          color='textSecondary'
                          sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            '& strong': {
                              backgroundColor: warning[100],
                              padding: '0 2px',
                              borderRadius: 1,
                            },
                          }}
                        >
                          {parse(getHighlightedText(option.highlights))}
                        </Typography>
                      </Stack>

                      <ChevronRight sx={{ color: isSelected ? '#007AFF' : 'inherit' }} />
                    </Box>
                  </Box>
                );
              })
            : searchInputValue &&
              searchInputValue.length > 2 && (
                <Typography
                  variant='body2'
                  color='textSecondary'
                  sx={{ p: 2 }}
                >
                  No results found
                </Typography>
              )}

          {/* Loading indicator for infinite scroll */}
          {searchQuery.isFetchingNextPage && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                p: 2,
              }}
            >
              <CircularProgress size={20} />
            </Box>
          )}
          {infiniteTrigger}
        </>
      )}
    </Box>
  );

  return (
    <LeftDrawer
      header={searchHeader}
      content={searchContent}
      footer={searchFooter}
      open={true}
      hasToolbar={true}
      noPadding={true}
    />
  );
};

export default SearchBarWithPersistentResults;
